"use client";

import { useState, memo, MouseEvent, useEffect, useRef, useMemo, useCallback, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Markdown } from "@/components/markdown";
import { CitationMarkdown } from "./CitationMarkdown";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRedditAlien, faYahoo } from "@fortawesome/free-brands-svg-icons";
import { SourceDisplay } from "./SourceDisplay";
import { AgentResultsCarousel } from "./AgentResultsCarousel";
import { UnifiedSearchCarousel } from "./UnifiedSearchCarousel";
import { FundAgentProgressCard } from "./FundAgentProgressCard";

import {
  CheckCircle,
  Clock,
  AlertCircle,
  Code2,
  FileText,
  Maximize2,
  Edit,
  Calendar,
  ChevronDown,
  ChevronUp,
  BarChart3,
  Layers,
  Play,
  Edit2,
  Search,
  List,
  Sparkles,
  Newspaper,
  Loader2,
  Download,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  XCircle,
  Terminal,
} from "lucide-react";
import { CodeBlock } from "./CodeBlock";
import { cn } from "@/lib/utils";
import { useArtifact } from "@/hooks/use-artifact";
import { ChartsTab } from "./tabs/ChartsTab";
import { SourcesTab } from "./tabs/SourcesTab";

interface PlotlyData {
  data: Array<any>;
  layout?: any;
  config?: any;
}

interface BokehData {
  html?: string;
  json?: any;
}

interface UnifiedSearchData {
  analysis: string;
  sources: Array<{
    text: string;
    author: string;
    score: number | null;
    url: string;
    post_title?: string;
    created_at: string;
    source_type: string;
  }>;
  title?: string;
  query?: string;
  search_type?: string;
  display_mode?: string;
  metadata?: any;
  date_range?: any;
  purpose?: string;
}

interface FundAgentData {
  dataframes: Record<string, {
    data: Array<Record<string, any>>;
    schema: {
      columns: string[];
      types: Record<string, string>;
      row_count: number;
    };
    sources: Array<{ name: string; url: string }>;
    reasoning: string;
  }>;
  sub_notebook_id: string;
  query: string;
}

interface SubAgentData {
  dataframes: Record<string, {
    data: Array<Record<string, any>>;
    schema: {
      columns: string[];
      types: Record<string, string>;
      row_count: number;
    };
    reasoning: string;
  }>;
  charts?: Array<{ plotly_json: any; title?: string }>;
  summary?: string;
  sub_notebook_id: string;
  task: string;
  functions: string[];
}

interface CellPreview {
  purpose: string;
  content: string;
  status: string;
  has_output: boolean;
}

interface SubNotebookProgress {
  status: string;
  cell_count?: number;
  name?: string;
  query?: string;
  index: number;
  cells?: CellPreview[];
}

interface CellSubProgress {
  cell_index: number;
  sub_notebook_ids: string[];
  sub_notebooks: Record<string, SubNotebookProgress>;
}

interface NotebookCellProps {
  cell: {
    id: string;
    index: number;
    cell_type: "code" | "markdown" | "search";
    purpose: string;
    content: string;
    outputs: Array<{
      type: "text" | "image" | "error" | "html" | "plotly" | "unified_search" | "signal_register" | "automations" | "bokeh" | "fund_agent" | "sub_agent" | "backtest-tabs" | "dashboard";
      data: string | PlotlyData | BokehData | UnifiedSearchData | FundAgentData | SubAgentData;
    }>;
    captured_params?: Array<{
      action: string;
      url?: string;
      filename?: string;
    }>;
    status: "success" | "executing" | "error" | "idle" | "canceled";
    sources?: Array<
      any & {
        source_type: string;
      }
    >;
  };
  onFullscreen?: (event: MouseEvent<HTMLElement>) => void;
  onEditCell?: () => void;
  isEditButtonShown?: boolean;
  isMinimal?: boolean;
  compact?: boolean;
  forceExpanded?: boolean;
  onRunCell?: (cellIndex: number) => Promise<void>;
  isRerunning?: boolean;
  isReadonly?: boolean;
  subProgress?: CellSubProgress;
  isReplay?: boolean;
  blurContent?: boolean;
}

// Helper: Convert YYYY-MM-DD to Unix timestamp
function dateToUnix(dateStr: string): number {
  return Math.floor(new Date(dateStr).getTime() / 1000);
}

// Helper: Map timespan to Yahoo Finance frequency
function getFrequency(timespan: string): string {
  if (timespan === "month") return "1mo";
  if (timespan === "week") return "1wk";
  return "1d";
}

// Helper: Build Yahoo Finance URLs and descriptions
interface YahooFinanceCard {
  url: string;
  description: string;
}

function buildYahooFinanceCards(capturedParams: Array<any>): YahooFinanceCard[] {
  const cards: YahooFinanceCard[] = [];
  
  for (const param of capturedParams) {
    if (param.action === "earnings" && param.ticker) {
      cards.push({
        url: `https://finance.yahoo.com/quote/${param.ticker}/analysis/`,
        description: `${param.ticker} earnings history and forecasts`
      });
    } else if (param.action === "options" && param.ticker && param.expiration_date && param.contract_type) {
      const unixDate = dateToUnix(param.expiration_date);
      const contractType = param.contract_type === "calls" ? "calls" : "puts";
      const displayType = param.contract_type === "calls" ? "call" : "put";
      cards.push({
        url: `https://finance.yahoo.com/quote/${param.ticker}/options/?date=${unixDate}&type=${contractType}`,
        description: `${param.ticker} ${displayType} options expiring ${param.expiration_date}`
      });
    } else if (param.action === "list_aggs" && param.ticker && param.start_date && param.end_date && param.timespan) {
      const period1 = dateToUnix(param.start_date);
      const period2 = dateToUnix(param.end_date);
      const frequency = getFrequency(param.timespan);
      cards.push({
        url: `https://finance.yahoo.com/quote/${param.ticker}/history/?period1=${period1}&period2=${period2}&frequency=${frequency}`,
        description: `${param.ticker} historical ${param.timespan} prices from ${param.start_date} to ${param.end_date}`
      });
      // Add additional trading chart card for list_aggs
      cards.push({
        url: `https://finance.yahoo.com/quote/${param.ticker}/`,
        description: `${param.ticker} trading chart`
      });
    }
  }
  
  return cards;
}

// Yahoo Finance Hover Card Component
function YahooFinanceHoverCard({ 
  cards, 
  iconRect, 
  onClose,
  timeoutRef,
  currentIndex,
  onIndexChange
}: { 
  cards: YahooFinanceCard[]; 
  iconRect: DOMRect | null;
  onClose: () => void;
  timeoutRef: React.MutableRefObject<NodeJS.Timeout | null>;
  currentIndex: number;
  onIndexChange: (index: number) => void;
}) {
  if (cards.length === 0 || !iconRect) return null;
  
  const currentCard = cards[currentIndex];
  
  // Memoize position calculations
  const position = useMemo(() => ({
    top: iconRect.bottom + 4,
    right: window.innerWidth - iconRect.right
  }), [iconRect]);
  
  const handlePrev = useCallback((e: MouseEvent) => {
    e.stopPropagation();
    onIndexChange((currentIndex - 1 + cards.length) % cards.length);
  }, [currentIndex, cards.length, onIndexChange]);
  
  const handleNext = useCallback((e: MouseEvent) => {
    e.stopPropagation();
    onIndexChange((currentIndex + 1) % cards.length);
  }, [currentIndex, cards.length, onIndexChange]);
  
  const handleMouseEnter = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  }, [timeoutRef]);
  
  const handleMouseLeave = useCallback(() => {
    timeoutRef.current = setTimeout(() => {
      onClose();
    }, 100);
  }, [timeoutRef, onClose]);
  
  const cardContent = (
    <div
      className="fixed z-50 w-64 bg-black border border-border rounded-lg shadow-lg p-3 cursor-pointer"
      style={{ top: `${position.top}px`, right: `${position.right}px` }}
      onClick={() => window.open(currentCard.url, '_blank')}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Header */}
      <div className="flex items-center gap-2 mb-2">
        <FontAwesomeIcon icon={faYahoo} className="w-4 h-4 text-purple-500" />
        <span className="text-sm font-medium text-white">Yahoo Finance</span>
      </div>
      
      {/* Description */}
      <div className="text-xs text-gray-300 mb-2">
        {currentCard.description}
      </div>
      
      {/* Footer with external link icon and pagination */}
      <div className="flex items-center justify-between">
        <ExternalLink className="w-3 h-3 text-gray-400" />
        
        {cards.length > 1 && (
          <div className="flex items-center gap-2">
            <button
              onClick={handlePrev}
              className="hover:bg-muted rounded p-0.5"
            >
              <ChevronLeft className="w-3 h-3 text-gray-400" />
            </button>
            <span className="text-xs text-gray-400">
              {currentIndex + 1}/{cards.length}
            </span>
            <button
              onClick={handleNext}
              className="hover:bg-muted rounded p-0.5"
            >
              <ChevronRight className="w-3 h-3 text-gray-400" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
  
  return createPortal(cardContent, document.body);
}

function NotebookCellComponent({
  cell,
  onFullscreen,
  onEditCell,
  isEditButtonShown = false,
  isMinimal = false,
  compact = false,
  forceExpanded = false,
  isReplay = false,
  onRunCell,
  isRerunning = false,
  isReadonly = false,
  subProgress,
  blurContent = false,
}: NotebookCellProps) {
  const { setArtifact } = useArtifact();

  // Local state for cell execution
  const [isExecuting, setIsExecuting] = useState(false);
  
  // Yahoo Finance hover card state
  const [showYahooCard, setShowYahooCard] = useState(false);
  const [yahooIconRect, setYahooIconRect] = useState<DOMRect | null>(null);
  const [yahooCardIndex, setYahooCardIndex] = useState(0);
  const yahooCardTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const yahooIconRef = useRef<HTMLDivElement | null>(null);
  
  // Build Yahoo Finance cards from captured_params
  const yahooCards = useMemo(() => {
    if (!cell.captured_params) return [];
    return buildYahooFinanceCards(cell.captured_params);
  }, [cell.captured_params]);
  
  // Handle Yahoo card hover with delay
  const handleYahooMouseEnter = () => {
    if (yahooCardTimeoutRef.current) {
      clearTimeout(yahooCardTimeoutRef.current);
    }
    // Update icon position
    if (yahooIconRef.current) {
      setYahooIconRect(yahooIconRef.current.getBoundingClientRect());
    }
    setShowYahooCard(true);
  };
  
  const handleYahooMouseLeave = () => {
    yahooCardTimeoutRef.current = setTimeout(() => {
      setShowYahooCard(false);
    }, 100); // 300ms delay
  };

  // Determine available output types and auto-select tab
  const availableOutputTypes = useMemo(() => {
    const hasOutputs = cell.outputs && cell.outputs.length > 0;
    
    // NOTE: fund_agent visuals are shown in Analysis tab carousel, not Charts tab
    const hasChartOutputs = hasOutputs && cell.outputs.some(o => 
      o.type === 'plotly' || o.type === 'bokeh' || o.type === 'html' || o.type === 'image' || o.type === 'backtest-tabs' || o.type === 'dashboard'
    );
    
    const hasText = hasOutputs && cell.outputs.some(o => o.type === 'text');
    const hasFundAgentOutputs = hasOutputs && cell.outputs.some(o => o.type === 'fund_agent');
    const hasSubAgentOutputs = hasOutputs && cell.outputs.some(o => o.type === 'sub_agent');
    const hasUnifiedSearch = hasOutputs && cell.outputs.some(o => o.type === 'unified_search');
    
    // Show analysis tab if: fund_agent/sub_agent outputs exist OR subProgress exists (sub-agents working)
    const hasAnalysis = hasFundAgentOutputs || hasSubAgentOutputs || hasUnifiedSearch || !!subProgress;
    
    const types = {
      // Show charts tab if any chart-type outputs exist (plotly, bokeh, html, image)
      // fund_agent visuals are shown in the Analysis tab carousel instead
      charts: hasChartOutputs,
      text: hasText,
      // stdout shows when there's text output alongside analysis (fund_agent)
      stdout: hasText && (hasFundAgentOutputs || hasSubAgentOutputs || hasUnifiedSearch),
      analysis: hasAnalysis,
      unifiedSearch: hasUnifiedSearch,
      fundAgent: hasFundAgentOutputs,
    };
    
    return types;
  }, [cell.outputs, subProgress]);

  const getDefaultTab = useCallback(() => {
    if (cell.cell_type === "search") return "results";
    if (cell.cell_type === "code") {
      // Priority: Analysis (sub-agent summary) → Charts (visuals) → Text → Code
      if (availableOutputTypes.analysis) return "analysis";
      if (availableOutputTypes.charts) return "charts";
      // In compact/chat mode, skip text and code tabs (they're hidden)
      if (compact) return availableOutputTypes.text ? "text" : "code";
      if (availableOutputTypes.text) return "text";
      // In replay mode with no code content, default to text instead of code
      if (isReplay && !cell.content) return "text";
      return "code"; // Default to code if no outputs
    }
    return "output";
  }, [cell.cell_type, availableOutputTypes, compact, isReplay, cell.content]);

  const [activeTab, setActiveTab] = useState(() => getDefaultTab());
  const [userSelectedTab, setUserSelectedTab] = useState(false);
  const prevStatusRef = useRef(cell.status);

  // Auto-switch to best tab when cell finishes executing OR when outputs upgrade (e.g. SWR hydration brings charts).
  // Keyed on a VALUE signature, not cell.outputs reference: during streaming the
  // outputs array is a new reference on every tick, which re-ran this effect
  // constantly and could storm re-renders on mobile (React #185 crashes seen in
  // prod telemetry Aug 2026). The signature only changes when the set of
  // available tabs or the cell status actually changes.
  const tabSignature = `${cell.status}|${availableOutputTypes.analysis ? 1 : 0}${availableOutputTypes.charts ? 1 : 0}${availableOutputTypes.text ? 1 : 0}`;
  const lastAppliedTabSigRef = useRef<string | null>(null);
  useEffect(() => {
    prevStatusRef.current = cell.status;
    if (userSelectedTab) return;
    if (lastAppliedTabSigRef.current === tabSignature) return;
    lastAppliedTabSigRef.current = tabSignature;

    const bestTab = getDefaultTab();
    const priority: Record<string, number> = { code: 0, text: 1, charts: 2, analysis: 3, results: 3 };
    setActiveTab(current => {
      if ((priority[bestTab] ?? 0) > (priority[current] ?? 0)) {
        return bestTab;
      }
      return current;
    });
  }, [tabSignature, cell.status, userSelectedTab, getDefaultTab]);

  // Auto-switch to analysis tab when subProgress first appears
  const prevSubProgressRef = useRef<typeof subProgress>(undefined);
  useEffect(() => {
    if (subProgress && !prevSubProgressRef.current && !userSelectedTab) {
      setActiveTab("analysis");
    }
    prevSubProgressRef.current = subProgress;
  }, [subProgress, userSelectedTab]);
  
  // Helper to handle manual tab selection
  const handleTabClick = (tab: string) => {
    setActiveTab(tab);
    setUserSelectedTab(true);
  };
  
  const [sortOrder, setSortOrder] = useState<"newest" | "oldest">("newest");
  const [selectedFilters, setSelectedFilters] = useState<string[]>([
    "reddit",
    "twitter",
    "news",
  ]);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [highlightedSourceIndex, setHighlightedSourceIndex] = useState<
    number | null
  >(null);
  const [contentView, setContentView] = useState<"preview" | "expanded">("preview");
  const [isContentOverflowing, setIsContentOverflowing] = useState(false);
  const filterRef = useRef<HTMLDivElement>(null);
  const sourcesContainerRef = useRef<HTMLDivElement>(null);
  const [contentEl, setContentEl] = useState<HTMLDivElement | null>(null);
  const setContentRef = useCallback((node: HTMLDivElement | null) => {
    setContentEl(node);
  }, []);

  // Get all unified search outputs (batch can produce multiple)
  const unifiedSearchOutputs = useMemo(() => {
    return cell.outputs?.filter(o => o.type === 'unified_search').map(o => o.data as UnifiedSearchData) || [];
  }, [cell.outputs]);

  // Get fund agent data from outputs if available
  const fundAgentOutputs = useMemo(() => {
    const result = cell.outputs?.filter(o => o.type === 'fund_agent').map(o => o.data as FundAgentData) || [];
    return result;
  }, [cell.outputs]);

  // Get sub agent data from outputs if available
  const subAgentOutputs = useMemo(() => {
    const result = cell.outputs?.filter(o => o.type === 'sub_agent').map(o => o.data as SubAgentData) || [];
    if (result.length > 0) {
      result.forEach((sa, i) => {
      });
    }
    return result;
  }, [cell.outputs]);

  // Build unified agent results for the outer carousel (both types as slides)
  const agentResults = useMemo(() => {
    const results: Array<{ type: 'fund_agent'; data: FundAgentData } | { type: 'sub_agent'; data: SubAgentData }> = [];
    for (const fa of fundAgentOutputs) results.push({ type: 'fund_agent', data: fa });
    for (const sa of subAgentOutputs) results.push({ type: 'sub_agent', data: sa });
    return results;
  }, [fundAgentOutputs, subAgentOutputs]);

  // Map sources with their original indices for citation reference
  // Use unified search sources if available, otherwise use cell.sources
  const sourcesWithOriginalIndex = useMemo(() => {
    const sources = unifiedSearchOutputs.length > 0
      ? unifiedSearchOutputs.flatMap(d => d.sources || [])
      : cell.sources;
    if (!sources) return [];
    return sources.map((source, index) => ({
      ...source,
      originalIndex: index + 1, // Citations are 1-indexed
    }));
  }, [cell.sources, unifiedSearchOutputs]);

  // Detect if cell has dashboard output
  const hasDashboard = useMemo(() => {
    return cell.outputs?.some(o => o.type === 'dashboard') ?? false;
  }, [cell.outputs]);

  // Memoize chart outputs to prevent unnecessary re-renders
  const chartOutputs = useMemo(() => {
    if (!cell.outputs || cell.outputs.length === 0) return [];

    // Only treat HTML as chart output if it is the backtest-tabs component
    const isChartLikeHtml = (output: { type: string; data: any }) => {
      if (output.type !== "html" || typeof output.data !== "string") return false;
      const normalized = output.data.replace(/\s+/g, " ").trim().toLowerCase();
      return (
        normalized.includes("data-visualization-type=\"backtest-tabs\"") &&
        normalized.includes("data-xynth-component=\"true\"")
      );
    };

    // NOTE: fund_agent visuals are NOT extracted here anymore
    // They stay with their queries in the FundAgentResultsCarousel component

    const hasCharts = cell.outputs.some((output) =>
      output.type === "plotly" ||
      output.type === "bokeh" ||
      output.type === "backtest-tabs" ||
      output.type === "dashboard" ||
      isChartLikeHtml(output as any)
    );

    // Log dashboard detection
    const dashboardCount = cell.outputs.filter(o => o.type === "dashboard").length;
    if (dashboardCount > 0) {
    }

    // If there are charts, show only charts; otherwise show images
    return cell.outputs.filter((output) => {
      if (hasCharts) {
        if (output.type === "plotly" || output.type === "bokeh" || output.type === "backtest-tabs" || output.type === "dashboard") return true;
        return isChartLikeHtml(output as any);
      }
      return output.type === "image";
    });
  }, [cell.outputs]);

  // Handle citation click: navigate directly to the source URL (no scrolling)
  const handleCitationClick = useCallback((citationNumber: number) => {
    const target = sourcesWithOriginalIndex.find(
      (s) => s.originalIndex === citationNumber
    );
    if (!target || !target.url) {
      return;
    }
    try {
      window.open(target.url, "_blank", "noopener,noreferrer");
    } catch (e) {
      // Fallback: navigate current tab
      window.location.href = target.url;
    }
  }, [sourcesWithOriginalIndex]);

  // Reset contentView when switching tabs
  useEffect(() => {
    setContentView("preview");
  }, [activeTab]);

  useEffect(() => {
    if (forceExpanded) {
      setContentView("expanded");
    }
  }, [forceExpanded, activeTab]);

  // Unified overflow detection using ResizeObserver (standard approach)
  useLayoutEffect(() => {
    if (!contentEl) return;

    const checkOverflow = () => {
      const maxHeight = window.innerHeight * 0.9; // 90vh in pixels
      const actualHeight = contentEl.scrollHeight;
      setIsContentOverflowing(actualHeight > maxHeight);
    };

    const resizeObserver = new ResizeObserver(() => {
      checkOverflow();
    });
    resizeObserver.observe(contentEl);

    const raf = requestAnimationFrame(checkOverflow);
    const to = setTimeout(checkOverflow, 50);

    return () => {
      resizeObserver.disconnect();
      cancelAnimationFrame(raf);
      clearTimeout(to);
    };
  }, [contentEl, contentView]);

  // Removed scroll-on-mount effect since we now navigate directly to the URL


  // Close filter dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        filterRef.current &&
        !filterRef.current.contains(event.target as Node)
      ) {
        setIsFilterOpen(false);
      }
    };

    if (isFilterOpen) {
      document.addEventListener("mousedown", handleClickOutside as any);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside as any);
    }
  }, [isFilterOpen]);

  const getStatusIcon = () => {
    // Show executing icon if cell is currently executing (local state)
    if (isExecuting) {
      return <Clock className="h-4 w-4 text-blue-500 animate-spin" />;
    }
    
    switch (cell.status) {
      case "success":
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case "executing":
        return <Clock className="h-4 w-4 text-blue-500 animate-spin" />;
      case "error":
        return <AlertCircle className="h-4 w-4 text-red-500" />;
      case "canceled":
        return <XCircle className="h-4 w-4 text-yellow-500" />;
      default:
        return (
          <div className="h-4 w-4 rounded-full border-2 border-muted-foreground" />
        );
    }
  };

  // Helper functions for source management
  const getFilteredAndSortedSources = () => {
    if (!sourcesWithOriginalIndex) return [];

    // Filter sources
    const filtered = sourcesWithOriginalIndex.filter((source) =>
      selectedFilters.includes(source.source_type)
    );

    // Sort sources
    return filtered.sort((a, b) => {
      const dateA = new Date(a.created_at).getTime();
      const dateB = new Date(b.created_at).getTime();
      return sortOrder === "newest" ? dateB - dateA : dateA - dateB;
    });
  };

  const getSourceCounts = () => {
    // Always include all three source types with 0 as default
    const counts: Record<string, number> = {
      twitter: 0,
      reddit: 0,
      news: 0,
    };
    
    if (sourcesWithOriginalIndex) {
      sourcesWithOriginalIndex.forEach((source) => {
        counts[source.source_type] = (counts[source.source_type] || 0) + 1;
      });
    }
    
    return counts;
  };

  const toggleFilter = (sourceType: string) => {
    setSelectedFilters((prev) =>
      prev.includes(sourceType)
        ? prev.filter((type) => type !== sourceType)
        : [...prev, sourceType]
    );
  };

  return (
    <Card
      data-cell-index={cell.index}
      data-cell-id={cell.id}
      className={cn(
        "overflow-x-hidden overflow-y-visible rounded-lg flex flex-col transition-all duration-300 ease-out bg-card w-full max-w-full min-w-0 relative",
        compact ? "mb-1.5 p-1" : "mb-6 p-2",
        isExecuting && "ring-2 ring-blue-500/50",
        cell.status === "canceled" && "opacity-60 grayscale"
      )}
    >
      {/* Executing indicator - subtle top border animation */}
      {(cell.status === "executing" || isExecuting) && (
        <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-blue-500 to-transparent animate-pulse" />
      )}
      
      <CardHeader className={cn("p-0 mt-0", compact ? "space-y-0.5 pb-0.5" : "space-y-2 pb-2")}>
        <div className="flex items-center justify-between">
          <div className={cn("flex items-center", compact ? "gap-2" : "gap-3")}>
            <Badge
              variant="outline"
              className="font-mono bg-secondary text-secondary-foreground shrink-0"
            >
              {cell.index}
            </Badge>
            {(() => {
              const prefixMatch = cell.purpose.match(
                /^(unified|social|financial|news)\s*\|\s*(.+)$/
              );
              if (prefixMatch) {
                const [, prefix, title] = prefixMatch;
                return (
                  <>
                    <h3
                      className="text-sm font-medium overflow-x-auto whitespace-nowrap max-w-[30vw]"
                      title={title}
                    >
                      {title}
                    </h3>
                  </>
                );
              }
              return (
                <h3
                  className="text-sm font-medium overflow-x-auto whitespace-nowrap max-w-[30vw]"
                  title={cell.purpose}
                >
                  {cell.purpose}
                </h3>
              );
            })()}
            <div className="shrink-0">{getStatusIcon()}</div>
          </div>
          <div className="flex items-center gap-1">
            {/* Download button - leftmost when available */}
            {(() => {
              const downloadParam = cell.captured_params?.find(p => p.action === "download");
              return downloadParam?.url && downloadParam?.filename ? (
                <a
                  href={downloadParam.url}
                  download={downloadParam.filename}
                  className={cn(
                    "h-6 w-6 p-0 inline-flex items-center justify-center rounded-md hover:bg-muted transition-colors bg-blue-500/20 animate-pulse"
                  )}
                  title={`Download ${downloadParam.filename}`}
                >
                  <Download className="h-4 w-4 text-blue-500" />
                </a>
              ) : null;
            })()}

            {/* Yahoo Finance icon with hover card */}
            {yahooCards.length > 0 && (
              <div
                ref={yahooIconRef}
                className="relative translate-y-[2px]"
                onMouseEnter={handleYahooMouseEnter}
                onMouseLeave={handleYahooMouseLeave}
              >
                <div className="h-6 w-6 p-0 inline-flex items-center justify-center rounded-md hover:bg-muted transition-colors">
                  <FontAwesomeIcon icon={faYahoo} className="w-4 h-4 text-purple-500" />
                </div>
              </div>
            )}
            {showYahooCard && (
              <YahooFinanceHoverCard 
                cards={yahooCards} 
                iconRect={yahooIconRect}
                onClose={() => setShowYahooCard(false)}
                timeoutRef={yahooCardTimeoutRef}
                currentIndex={yahooCardIndex}
                onIndexChange={setYahooCardIndex}
              />
            )}

            {/* Code cell navigation badges - icon only */}
            {cell.cell_type === "code" && (
              <>
                {/* Code Tab — hidden in compact/chat mode and in replay mode when content is stripped */}
                {!compact && !(isReplay && !cell.content) && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleTabClick("code")}
                  className={cn(
                    "h-6 w-6 p-0",
                    activeTab === "code"
                      ? "bg-secondary text-secondary-foreground"
                      : "hover:bg-muted"
                  )}
                  title="Code"
                >
                  <Code2 className="h-3 w-3" />
                </Button>
                )}

                {/* Output tabs - Hidden during execution */}
                {!isExecuting && (
                  <>
                    {/* Charts Tab - Only if plotly or image outputs exist */}
                    {availableOutputTypes.charts && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleTabClick("charts")}
                        className={cn(
                          "h-6 w-6 p-0",
                          activeTab === "charts"
                            ? "bg-secondary text-secondary-foreground"
                            : "hover:bg-muted"
                        )}
                        title="Charts"
                      >
                        <BarChart3 className="h-3 w-3" />
                      </Button>
                    )}

                    {/* Analysis Tab - Only if unified search output exists */}
                    {availableOutputTypes.analysis && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleTabClick("analysis")}
                        className={cn(
                          "h-6 w-6 p-0",
                          activeTab === "analysis"
                            ? "bg-secondary text-secondary-foreground"
                            : "hover:bg-muted"
                        )}
                        title="Analysis"
                      >
                        <Sparkles className="h-3 w-3" />
                      </Button>
                    )}

                    {/* Text Tab - Only if text outputs exist and NO analysis; hidden in compact/chat mode */}
                    {!compact && availableOutputTypes.text && !availableOutputTypes.analysis && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleTabClick("text")}
                        className={cn(
                          "h-6 w-6 p-0",
                          activeTab === "text"
                            ? "bg-secondary text-secondary-foreground"
                            : "hover:bg-muted"
                        )}
                        title="Text output"
                      >
                        <FileText className="h-3 w-3" />
                      </Button>
                    )}

                    {/* Terminal/stdout Tab - Show when text exists WITH analysis (fund_agent); hidden in compact/chat mode */}
                    {!compact && availableOutputTypes.stdout && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleTabClick("text")}
                        className={cn(
                          "h-6 w-6 p-0",
                          activeTab === "text"
                            ? "bg-secondary text-secondary-foreground"
                            : "hover:bg-muted"
                        )}
                        title="Terminal output"
                      >
                        <Terminal className="h-3 w-3" />
                      </Button>
                    )}

                    {/* Sources Tab - Only if unified search output exists */}
                    {availableOutputTypes.unifiedSearch && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleTabClick("sources")}
                        className={cn(
                          "h-6 w-6 p-0",
                          activeTab === "sources"
                            ? "bg-secondary text-secondary-foreground"
                            : "hover:bg-muted"
                        )}
                        title="Sources"
                      >
                        <List className="h-3 w-3" />
                      </Button>
                    )}
                  </>
                )}

                {/* Run button removed */}

              </>
            )}
            {/* Edit button removed */}
            {isMinimal && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onFullscreen}
                className="h-8 w-8 p-0 hover:bg-muted"
                title="Open in fullscreen"
              >
                <Maximize2 className="h-4 w-4" />
              </Button>
            )}
            {hasDashboard && !isMinimal && !compact && (
              <>
                <div className="h-4 w-px bg-border mx-0.5" />
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() =>
                    setArtifact((prev) => ({
                      ...prev,
                      isVisible: true,
                      workflowView: "dashboard",
                    }))
                  }
                  className="h-6 px-2 gap-1 text-xs font-medium hover:bg-white/10"
                  title="View Full Analysis"
                >
                  <Maximize2 className="h-3 w-3" />
                  <span>View Full Analysis</span>
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Navigation Badges - for search cells */}
        {cell.cell_type === "search" && (
          <div className="flex items-center justify-between"
          >
            <div className="flex items-center gap-2">
              <Badge
                variant="outline"
                className={`text-[10px] cursor-pointer transition-colors border px-1.5 py-0.5 ${
                  activeTab === "results"
                    ? "border-border bg-secondary text-secondary-foreground"
                    : "border-border bg-background text-foreground hover:bg-muted"
                }`}
                onClick={() => handleTabClick("results")}
              >
                <FileText className="h-3 w-3 mr-1" />
                Search Results
              </Badge>
              <Badge
                variant="outline"
                className={`text-[10px] cursor-pointer transition-colors border px-1.5 py-0.5 ${
                  activeTab === "sources"
                    ? "border-border bg-secondary text-secondary-foreground"
                    : "border-border bg-background text-foreground hover:bg-muted"
                }`}
                onClick={() => handleTabClick("sources")}
              >
                <div className="flex items-center -space-x-1 mr-1">
                  {/* Twitter/X icon */}
                  <div className="w-3 h-3 rounded-full bg-black border border-gray-400 flex items-center justify-center">
                    <svg
                      className="w-2 h-2 text-white"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                    </svg>
                  </div>
                  {/* Reddit icon */}
                  <div className="w-3 h-3 rounded-full bg-orange-600 border flex items-center justify-center">
                    <FontAwesomeIcon
                      icon={faRedditAlien}
                      className="w-2 h-2 text-white"
                    />
                  </div>
                  {/* News icon */}
                  <div className="w-3 h-3 rounded-full bg-gray-600 flex items-center justify-center">
                    <svg
                      className="w-2 h-2 text-white"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M19 5v14H5V5h14zm0-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2z" />
                      <path d="M14 17H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z" />
                    </svg>
                  </div>
                </div>
                Sources
              </Badge>
            </div>

            {/* Sort and Filter Controls - Only show for search cells on sources tab */}
            {activeTab === "sources" && (
              <div className="flex items-center gap-2">
                {/* Date Sort Button */}
                <Badge
                  variant="outline"
                  onClick={() =>
                    setSortOrder(sortOrder === "newest" ? "oldest" : "newest")
                  }
                  className="text-[10px] cursor-pointer transition-colors border border-border px-1.5 py-0.5 text-white bg-black hover:bg-muted"
                  title={`Sort by ${
                    sortOrder === "newest" ? "oldest" : "newest"
                  } first`}
                >
                  <Calendar className="w-3 h-3 mr-1" />
                  <ChevronDown
                    className={`w-3 h-3 transition-transform ${
                      sortOrder === "newest" ? "rotate-0" : "rotate-180"
                    }`}
                  />
                </Badge>

                {/* Filter Dropdown */}
                <div className="relative" ref={filterRef}>
                  <Badge
                    variant="outline"
                    onClick={() => setIsFilterOpen(!isFilterOpen)}
                    className="text-[10px] cursor-pointer transition-colors border border-border px-1.5 py-0.5 text-white bg-black hover:bg-muted"
                    title="Filter by source type"
                  >
                    <ChevronDown className="w-3 h-3 mr-1" />
                    {selectedFilters.length <= 3 && (
                      <span>{selectedFilters.length}</span>
                    )}
                  </Badge>

                  {isFilterOpen && (
                    <div className="absolute right-0 top-full mt-1 bg-black border border-border rounded-md shadow-lg z-50 min-w-[120px]">
                      {Object.entries(getSourceCounts()).map(
                        ([sourceType, count]) => (
                          <label
                            key={sourceType}
                            className="filter-dropdown-item flex items-center gap-2 px-3 py-2 text-[11px] text-white cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={selectedFilters.includes(sourceType)}
                              onChange={() => toggleFilter(sourceType)}
                              className="w-3 h-3 rounded"
                            />
                            <span className="capitalize">{sourceType}</span>
                            <span className="text-gray-400">({count})</span>
                          </label>
                        )
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </CardHeader>

      {/* Exported files — prominent per-file download chips. The header also
          shows a compact icon, but users never found a 24px icon and only the
          FIRST file got a link (`.find()`), so every exported file gets a
          visible chip here (Aug 2026). */}
      {(() => {
        const downloads = (cell.captured_params ?? []).filter(
          (p) => p.action === "download" && p.url && p.filename,
        );
        if (downloads.length === 0) return null;
        const displayName = (raw: string) => {
          // Strip the `_20260803_004029_9d94605d` uniqueness suffix for display.
          const m = raw.match(/^(.*?)_\d{8}_\d{6}_[0-9a-f]{8}(\.[A-Za-z0-9]+)$/);
          return m ? `${m[1]}${m[2]}` : raw;
        };
        return (
          <div className="flex flex-wrap items-center gap-2 px-3 pb-2">
            {downloads.map((p) => (
              <a
                key={p.url}
                href={p.url}
                download={p.filename}
                className="inline-flex items-center gap-1.5 rounded-md border border-border bg-secondary/60 px-2.5 py-1.5 text-xs text-foreground transition-colors hover:bg-secondary hover:border-blue-500/40 max-w-full"
                title={`Download ${p.filename}`}
              >
                <Download className="h-3.5 w-3.5 shrink-0 text-blue-500" />
                <span className="truncate max-w-[240px]">{displayName(p.filename!)}</span>
              </a>
            ))}
          </div>
        );
      })()}

      <CardContent className={cn("p-0 overflow-y-auto overflow-x-hidden transition-all duration-100 ease-out min-w-0 max-w-full flex-1 opacity-100", blurContent && "blur-md pointer-events-none select-none")}>
        {/* Content based on cell type */}
        {cell.cell_type === "search" ? (
          <>
            <AnimatePresence mode="wait">
              {activeTab === "results" && (
                <motion.div
                  key="results"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.2, ease: "easeOut" }}
                  className="rounded-lg w-full max-w-full min-w-0 overflow-hidden"
                >
                <div 
                  className={cn(
                    "overflow-hidden min-w-0 relative",
                    contentView === "preview" && "max-h-[90vh]"
                  )}
                >
                  <div ref={setContentRef} className="p-2">
                    <div className="prose prose-sm dark:prose-invert max-w-none [&_p]:whitespace-pre-wrap p-4">
                      <CitationMarkdown onCitationClick={handleCitationClick}>
                        {cell.content.replace(/\\n/g, "\n")}
                      </CitationMarkdown>
                    </div>
                  </div>
                  {contentView === "preview" && isContentOverflowing && (
                    <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-background via-background/80 to-transparent pointer-events-none" />
                  )}
                </div>
                {contentView === "preview" && isContentOverflowing && (
                  <div className="px-3 py-2 flex justify-center">
                    <button
                      onClick={() => setContentView("expanded")}
                      className="text-xs text-foreground bg-secondary hover:bg-secondary/80 border border-border rounded-md px-2 py-1 transition-all duration-200 flex items-center gap-1 shadow-sm"
                    >
                      Show More <ChevronDown className="w-4 h-4" />
                    </button>
                  </div>
                )}
                {contentView === "expanded" && (
                  <div className="px-3 py-2 flex justify-center">
                    <button
                      onClick={() => setContentView("preview")}
                      className="text-xs text-foreground bg-secondary hover:bg-secondary/80 border border-border rounded-md px-2 py-1 transition-all duration-200 flex items-center gap-1 shadow-sm"
                    >
                      Show Less <ChevronUp className="w-4 h-4" />
                    </button>
                  </div>
                )}
                </motion.div>
              )}
              {activeTab === "sources" && (
                <motion.div
                  key="sources-search"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.2, ease: "easeOut" }}
                  className="rounded-lg w-full max-w-full min-w-0 overflow-hidden"
                >
                <div 
                  className={cn(
                    "overflow-hidden min-w-0 relative",
                    contentView === "preview" && "max-h-[90vh]"
                  )}
                >
                  <div ref={setContentRef} className="p-2">
                    <div className="grid" style={{ gridTemplateColumns: 'minmax(0, 1fr)' }}>
                      <div ref={sourcesContainerRef}>
                        {(() => {
                          const filteredSources = getFilteredAndSortedSources();
                          return filteredSources.length > 0 ? (
                            <SourceDisplay
                              sources={filteredSources}
                              highlightedSourceIndex={highlightedSourceIndex}
                            />
                          ) : (
                            <div className="text-center py-8 text-muted-foreground">
                              {cell.sources && cell.sources.length > 0
                                ? "No sources match the current filters"
                                : "No sources available"}
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                  {contentView === "preview" && isContentOverflowing && (
                    <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-background via-background/80 to-transparent pointer-events-none" />
                  )}
                </div>
                {contentView === "preview" && isContentOverflowing && (
                  <div className="px-3 py-2 flex justify-center">
                    <button
                      onClick={() => setContentView("expanded")}
                      className="text-xs text-foreground bg-secondary hover:bg-secondary/80 border border-border rounded-md px-2 py-1 transition-all duration-200 flex items-center gap-1 shadow-sm"
                    >
                      Show More <ChevronDown className="w-4 h-4" />
                    </button>
                  </div>
                )}
                {contentView === "expanded" && (
                  <div className="px-3 py-2 flex justify-center">
                    <button
                      onClick={() => setContentView("preview")}
                      className="text-xs text-foreground bg-secondary hover:bg-secondary/80 border border-border rounded-md px-2 py-1 transition-all duration-200 flex items-center gap-1 shadow-sm"
                    >
                      Show Less <ChevronUp className="w-4 h-4" />
                    </button>
                  </div>
                )}
                </motion.div>
              )}
            </AnimatePresence>
          </>
        ) : cell.cell_type === "code" ? (
          // Code cell content
          <>
            <AnimatePresence mode="wait">
              {/* Code Tab Content */}
              {activeTab === "code" && (
                <motion.div
                  key="code"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.2, ease: "easeOut" }}
                  className="rounded-lg w-full max-w-full min-w-0 overflow-hidden"
                >
                <div 
                  className={cn(
                    "overflow-hidden min-w-0 relative",
                    contentView === "preview" && "max-h-[90vh]"
                  )}
                >
                  <div ref={setContentRef} className="grid" style={{ gridTemplateColumns: 'minmax(0, 1fr)' }}>
                    <div className="rounded-lg">
                      <CodeBlock code={cell.content} language="python" />
                    </div>
                  </div>
                  {contentView === "preview" && isContentOverflowing && (
                    <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-background via-background/80 to-transparent pointer-events-none" />
                  )}
                </div>
                {contentView === "preview" && isContentOverflowing && (
                  <div className="px-3 py-2 flex justify-center">
                    <button
                      onClick={() => setContentView("expanded")}
                      className="text-xs text-foreground bg-secondary hover:bg-secondary/80 border border-border rounded-md px-2 py-1 transition-all duration-200 flex items-center gap-1 shadow-sm"
                    >
                      Show More <ChevronDown className="w-4 h-4" />
                    </button>
                  </div>
                )}
                {contentView === "expanded" && (
                  <div className="px-3 py-2 flex justify-center">
                    <button
                      onClick={() => setContentView("preview")}
                      className="text-xs text-foreground bg-secondary hover:bg-secondary/80 border border-border rounded-md px-2 py-1 transition-all duration-200 flex items-center gap-1 shadow-sm"
                    >
                      Show Less <ChevronUp className="w-4 h-4" />
                    </button>
                  </div>
                )}
                </motion.div>
              )}

              {/* Charts Tab Content */}
              {activeTab === "charts" && cell.outputs && (
                <motion.div
                  key="charts"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.2, ease: "easeOut" }}
                  className="rounded-lg w-full max-w-full min-w-0 overflow-hidden"
                >
                <div 
                  className={cn(
                    "overflow-hidden min-w-0 relative",
                    hasDashboard
                      ? contentView === "preview" && "max-h-[400px]"
                      : contentView === "preview" && "max-h-[90vh]"
                  )}
                >
                  <div ref={setContentRef} className="grid" style={{ gridTemplateColumns: 'minmax(0, 1fr)' }}>
                    <ChartsTab chartOutputs={chartOutputs as Array<{ type: "plotly" | "bokeh" | "html" | "image" | "backtest-tabs" | "dashboard"; data: string | PlotlyData | BokehData }>} isReplay={isReplay} />
                  </div>
                  {hasDashboard && contentView === "preview" ? (
                    <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-background via-background/80 to-transparent pointer-events-none" />
                  ) : contentView === "preview" && isContentOverflowing ? (
                    <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-background via-background/80 to-transparent pointer-events-none" />
                  ) : null}
                </div>
                {!hasDashboard && contentView === "preview" && isContentOverflowing && (
                  <div className="px-3 py-2 flex justify-center">
                    <button
                      onClick={() => setContentView("expanded")}
                      className="text-xs text-foreground bg-secondary hover:bg-secondary/80 border border-border rounded-md px-2 py-1 transition-all duration-200 flex items-center gap-1 shadow-sm"
                    >
                      Show More <ChevronDown className="w-4 h-4" />
                    </button>
                  </div>
                )}
                {!hasDashboard && contentView === "expanded" && (
                  <div className="px-3 py-2 flex justify-center">
                    <button
                      onClick={() => setContentView("preview")}
                      className="text-xs text-foreground bg-secondary hover:bg-secondary/80 border border-border rounded-md px-2 py-1 transition-all duration-200 flex items-center gap-1 shadow-sm"
                    >
                      Show Less <ChevronUp className="w-4 h-4" />
                    </button>
                  </div>
                )}
                </motion.div>
              )}

              {/* Analysis Tab Content - For unified search, fund agent, sub-agent, or sub-agent progress */}
              {activeTab === "analysis" && (unifiedSearchOutputs.length > 0 || agentResults.length > 0 || subProgress) && (
                <motion.div
                  key="analysis"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.2, ease: "easeOut" }}
                  className="rounded-lg w-full max-w-full min-w-0 overflow-hidden"
                >
                <div 
                  className={cn(
                    "overflow-hidden min-w-0 relative",
                    contentView === "preview" && "max-h-[90vh]"
                  )}
                >
                  <div ref={setContentRef} className="grid" style={{ gridTemplateColumns: 'minmax(0, 1fr)' }}>
                    <div className="rounded-lg">
                      <div className="p-4 max-w-none">
                        {/* Unified Search Analysis - Carousel */}
                        {unifiedSearchOutputs.length > 0 && (
                          <UnifiedSearchCarousel
                            results={unifiedSearchOutputs}
                            onCitationClick={handleCitationClick}
                          />
                        )}
                        {/* Agent Results - Unified Carousel (fund + sub agents) */}
                        {agentResults.length > 0 && unifiedSearchOutputs.length === 0 && (
                          <AgentResultsCarousel results={agentResults} />
                        )}
                        {/* Progress Card - Show while sub-agents working */}
                        {(() => {
                          const showProgress = subProgress && agentResults.length === 0 && unifiedSearchOutputs.length === 0;
                          const showResults = agentResults.length > 0 && unifiedSearchOutputs.length === 0;
                          return null;
                        })()}
                        {subProgress && agentResults.length === 0 && unifiedSearchOutputs.length === 0 && (
                          <FundAgentProgressCard subNotebooks={subProgress.sub_notebooks} />
                        )}
                      </div>
                    </div>
                  </div>
                  {contentView === "preview" && isContentOverflowing && (
                    <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-background via-background/80 to-transparent pointer-events-none" />
                  )}
                </div>
                {contentView === "preview" && isContentOverflowing && (
                  <div className="px-3 py-2 flex justify-center">
                    <button
                      onClick={() => setContentView("expanded")}
                      className="text-xs text-foreground bg-secondary hover:bg-secondary/80 border border-border rounded-md px-2 py-1 transition-all duration-200 flex items-center gap-1 shadow-sm"
                    >
                      Show More <ChevronDown className="w-4 h-4" />
                    </button>
                  </div>
                )}
                {contentView === "expanded" && (
                  <div className="px-3 py-2 flex justify-center">
                    <button
                      onClick={() => setContentView("preview")}
                      className="text-xs text-foreground bg-secondary hover:bg-secondary/80 border border-border rounded-md px-2 py-1 transition-all duration-200 flex items-center gap-1 shadow-sm"
                    >
                      Show Less <ChevronUp className="w-4 h-4" />
                    </button>
                  </div>
                )}
                </motion.div>
              )}

              {/* Text Tab Content */}
              {activeTab === "text" && cell.outputs && (
                <motion.div
                  key="text"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.2, ease: "easeOut" }}
                  className="rounded-lg w-full max-w-full min-w-0 overflow-hidden"
                >
                <div 
                  className={cn(
                    "overflow-hidden min-w-0 relative",
                    contentView === "preview" && "max-h-[90vh]"
                  )}
                >
                  <div ref={setContentRef} className="grid" style={{ gridTemplateColumns: 'minmax(0, 1fr)' }}>
                    <div className="rounded-lg">
                      <pre className="p-4 whitespace-pre-wrap break-words font-mono text-sm leading-relaxed text-foreground">
                        {cell.outputs
                          .filter(output => output.type === 'text')
                          .map((output, index) => output.data as string)
                          .join('\n\n')}
                      </pre>
                    </div>
                  </div>
                  {contentView === "preview" && isContentOverflowing && (
                    <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-background via-background/80 to-transparent pointer-events-none" />
                  )}
                </div>
                {contentView === "preview" && isContentOverflowing && (
                  <div className="px-3 py-2 flex justify-center">
                    <button
                      onClick={() => setContentView("expanded")}
                      className="text-xs text-foreground bg-secondary hover:bg-secondary/80 border border-border rounded-md px-2 py-1 transition-all duration-200 flex items-center gap-1 shadow-sm"
                    >
                      Show More <ChevronDown className="w-4 h-4" />
                    </button>
                  </div>
                )}
                {contentView === "expanded" && (
                  <div className="px-3 py-2 flex justify-center">
                    <button
                      onClick={() => setContentView("preview")}
                      className="text-xs text-foreground bg-secondary hover:bg-secondary/80 border border-border rounded-md px-2 py-1 transition-all duration-200 flex items-center gap-1 shadow-sm"
                    >
                      Show Less <ChevronUp className="w-4 h-4" />
                    </button>
                  </div>
                )}
                </motion.div>
              )}

              {/* Sources Tab Content - Only for unified search */}
              {activeTab === "sources" && unifiedSearchOutputs.length > 0 && (
                <SourcesTab
                  sourcesWithOriginalIndex={sourcesWithOriginalIndex}
                  highlightedSourceIndex={highlightedSourceIndex}
                  sortOrder={sortOrder}
                  setSortOrder={setSortOrder}
                  selectedFilters={selectedFilters}
                  toggleFilter={toggleFilter}
                  isFilterOpen={isFilterOpen}
                  setIsFilterOpen={setIsFilterOpen}
                  contentView={contentView}
                  setContentView={setContentView}
                  isContentOverflowing={isContentOverflowing}
                  contentRefCallback={setContentRef}
                  sourcesContainerRef={sourcesContainerRef}
                  filterRef={filterRef}
                />
              )}
            </AnimatePresence>
          </>
        ) : cell.cell_type === "markdown" ? (
          // Markdown cell
          <div className="p-4 prose prose-sm dark:prose-invert max-w-none [&>*:first-child]:mt-0  rounded-lg overflow-hidden">
            <Markdown>{cell.content}</Markdown>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

// Memoize the component to prevent unnecessary re-renders
export const NotebookCell = memo(NotebookCellComponent);
